import { Typography } from '@cannect/new-components/atoms'
import { CustomSelect } from '@cannect/new-components/atoms/Form/CustomSelect'
import { PrescriberDetailCard, SchedulingFeaturesSections } from '@cannect/new-components/organisms'
import {
  TSchedulingFilterByType,
  TSchedulingFilterSortType,
  useGetAvailablePrescribers
} from '@cannect/services/resources/scheduling'
import { Skeleton } from 'antd'
import { AnimatePresence, motion } from 'framer-motion'
import { useEffect, useState } from 'react'
import { useLocation } from 'react-router'
import FilterForm from './FilterForm'
import InstantConsultationBanner from '@cannect/new-components/InstantConsultationBanner'

export default function FilterByStep() {
  const location = useLocation()
  const params = new URLSearchParams(location.search)

  const filterBy = params.get('filter_by') as TSchedulingFilterByType
  const filterById = params.get('filter_by_id')
  const attendsChildren = params.get('attends_children') === 'true'

  const [sortBy, setSortBy] = useState<TSchedulingFilterSortType>('available_date_times')

  const currentFilterBy = {
    type: filterBy || 'expertise',
    value: filterById || undefined
  }

  const { data: availablePrescribers, isFetching: loadingPrescribers } = useGetAvailablePrescribers({
    filterBy: currentFilterBy,
    sortBy,
    orderBy: 'asc',
    attendsChildren
  })

  const prescribersList = availablePrescribers?.prescribers || []
  const showPrescribersList = prescribersList.length > 0

  const SORT_OPTIONS = [
    { label: 'Ordem alfabética', value: 'professional_name' },
    { label: 'Menor valor de consulta', value: 'online_appointment_value' },
    { label: 'Horários disponíveis', value: 'available_date_times' }
  ]

  useEffect(() => {
    setSortBy('available_date_times')
  }, [filterBy, filterById, attendsChildren])

  return (
    <div className="w-full max-w-full bg-secondary-100">
      <section
        className={
          showPrescribersList
            ? 'bg-[#D0D7A7]'
            : 'bg-scheduling-hero-mobile bg-cover bg-[80%] bg-no-repeat sm:bg-center md:bg-scheduling-hero-desktop'
        }>
        <div
          className={`container flex max-w-[1164px] flex-col items-center justify-start gap-8 px-4 transition-all md:flex-row md:justify-between ${!showPrescribersList ? 'min-h-[calc(100dvh-74px)] py-2 md:min-h-[calc(100dvh-106px)] md:py-24' : 'pb-8 pt-12'}`}>
          <div className="flex w-full flex-col gap-6">
            <AnimatePresence initial={false} mode="popLayout">
              {!showPrescribersList && (
                <motion.div
                  key="hero-content"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{
                    duration: 0.3,
                    exit: { duration: 0.1 }
                  }}>
                  <Typography.Heading
                    type="headingTwo"
                    weight="semibold"
                    className="leading-15 mb-4 mt-20 text-primary md:mb-0 md:mt-0">
                    Sua consulta em poucos cliques
                  </Typography.Heading>
                  <Typography.Text className="mt-4 text-primary md:mt-0">
                    Bem-vindo(a) ao nosso agendamento digital! Encontre<span className="md:hidden"> </span>
                    <br className="hidden md:block" />
                    um especialista na terapia canabinoide{' '}
                    <span className="font-semibold text-primary-1000">a partir de R$ 150,00</span> no pix.
                  </Typography.Text>
                </motion.div>
              )}
            </AnimatePresence>

            <FilterForm isLoading={loadingPrescribers} showFilterByDate={showPrescribersList} />

            <InstantConsultationBanner
              className=""
              title="Quer ser atendido agora?"
              description="Sem espera. Conecte-se imediatamente com um médico generalista disponível."
            />

            <AnimatePresence initial={false}>
              {showPrescribersList && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5 }}
                  className="mt-4 flex items-center justify-between gap-2">
                  <div className="flex w-full max-w-fit flex-col -space-y-2 md:flex-row md:items-center md:gap-2 md:space-y-0">
                    <Typography.Text>Ordenar por:</Typography.Text>
                    <CustomSelect
                      value={SORT_OPTIONS.find((opt) => opt.value === sortBy)}
                      onChange={(option) => option && setSortBy(option.value as TSchedulingFilterSortType)}
                      options={SORT_OPTIONS}
                      placeholder="Ordenar por"
                      variant="outlined"
                      customClassNames={{
                        control: 'bg-transparent border-none px-0 h-auto gap-1 text-base font-semibold',
                        dropdownIndicator: 'text-gray-800',
                        singleValue: 'text-neutral-800 text-base',
                        placeholder: 'text-gray-800'
                      }}
                    />
                  </div>
                  <Typography.Text className="text-right">
                    <strong>{prescribersList?.length}</strong> profission
                    {prescribersList.length === 1 ? 'al' : 'ais'} encontrado
                    {prescribersList.length === 1 ? '' : 's'}
                  </Typography.Text>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </section>

      <AnimatePresence initial={false}>
        {showPrescribersList ? (
          <motion.div
            key="prescribers-list"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="container mx-auto flex w-full flex-col py-8">
            {loadingPrescribers ? (
              <div className="w-full">
                <Skeleton active paragraph={{ rows: 4 }} round />
              </div>
            ) : (
              <div className="mx-auto max-w-[1164px] space-y-16">
                {prescribersList.length > 0 ? (
                  prescribersList.map((prescriber, index) => (
                    <motion.div
                      key={prescriber.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{
                        duration: 0.5,
                        delay: index * 0.1
                      }}>
                      <PrescriberDetailCard prescriberInfo={prescriber} />
                    </motion.div>
                  ))
                ) : (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5 }}
                    className="flex h-40 items-center justify-center rounded-lg border border-neutral-200">
                    <Typography.Text type="paragraphOne" muted>
                      Nenhum profissional encontrado.
                    </Typography.Text>
                  </motion.div>
                )}
              </div>
            )}
          </motion.div>
        ) : (
          <motion.div
            key="features-section"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, exit: { duration: 0.1 } }}>
            <SchedulingFeaturesSections />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
